// Criteria-builder grid — v4 recompose of src/pages/rubrics/components/
// RubricsCustomComponent (+ RubricCriterionCell / RubricDescriptionCell /
// RubricLevelHeader) on v4 primitives. Fully CONTROLLED (RHF Controller
// value/onChange), unlike the v3 internal-state + sync-effect hybrid — same
// state shape and handler semantics:
//   { settings: {…}, criteria: [{ title, label, desc, strands, levels_json:
//     [{ id, level_points, level_label, desc }], _isNew?, _destroy? }] }
// one_level_for_all: add/remove level applies across every criterion;
// criterion titles auto-assign A, B, C… skipping _destroy rows.
import { getUniqueId, removeHTMLTagsFromText } from '@/lib/commonHelpers';
import { AddSquareLinear, CloseSquareLinear } from '@assessprep/icons/solar';
import { useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';

interface RubricLevel {
  id: number;
  level_points: string | number;
  level_label: string;
  desc: string;
}

interface RubricCriterion {
  id?: string | number;
  title: string;
  label: string;
  desc: string;
  strands: any[];
  levels_json: RubricLevel[] | Record<string, never>;
  rubric_id?: number;
  _isNew?: boolean;
  _destroy?: boolean;
  [key: string]: any;
}

interface RubricCriteriaState {
  settings: { use_scoring: boolean; one_level_for_all: boolean; strands: boolean };
  criteria: RubricCriterion[];
}

export interface RubricCriteriaBuilderProps {
  value?: any;
  onChange: (value: RubricCriteriaState) => void;
  showScoring?: boolean;
  /** Current rubric id (edit mode) — stamped onto new criteria (v3 parity). */
  rubricId?: string | number;
  /** Rubric type — drives the custom-family 1-indexed level-id law (APL-11550). */
  typeC?: string;
  'data-testid'?: string;
}

const getAlphabetByIndex = (index: number) => String.fromCharCode(65 + index);

// Custom rubrics use 1-indexed level ids (1..N): the growth analytics writer
// derives max_level from the level COUNT, so the top ordinal must equal N to
// reach pct=1.0. Non-custom types keep 0-based ids (persisted levels_json
// unchanged) — APL-11550 spec c1a4cbeb.
const getLevelStartId = (typeC?: string) => (typeC === 'custom' ? 1 : 0);

const emptyLevel = (id = 0): RubricLevel => ({ id, level_points: '', level_label: '', desc: '' });

const getDefaultState = (typeC?: string): RubricCriteriaState => ({
  settings: { use_scoring: false, one_level_for_all: true, strands: false },
  criteria: [
    {
      title: 'A',
      label: '',
      desc: '',
      strands: [],
      levels_json: [emptyLevel(getLevelStartId(typeC))],
    },
  ],
});

const isValidState = (value: any): boolean =>
  value && Array.isArray(value.criteria) && value.criteria.length > 0;

// levels_json arrives as `{}` for myp_achievement_level rows — normalize to
// one editable level (v3 normalizeCriteria parity).
const normalizeCriteria = (criteria: any[], typeC?: string): RubricCriterion[] =>
  criteria.map((criterion: any) => ({
    ...criterion,
    levels_json: Array.isArray(criterion.levels_json)
      ? criterion.levels_json
      : [emptyLevel(getLevelStartId(typeC))],
  }));

const normalizeSettings = (settings: any): RubricCriteriaState['settings'] => {
  const defaults = { use_scoring: false, one_level_for_all: true, strands: false };
  if (!settings || typeof settings === 'string') return defaults;
  return { ...defaults, ...settings };
};

// Auto-assign criterion titles A, B, C… over the active rows (v3 parity —
// mutates the passed clones, which are already fresh copies).
const withTitles = (criteria: RubricCriterion[]): RubricCriterion[] => {
  let index = 0;
  criteria.forEach((criterion) => {
    if (!criterion._destroy) {
      criterion.title = getAlphabetByIndex(index);
      index += 1;
    }
  });
  return criteria;
};

export const RubricCriteriaBuilder = ({
  value,
  onChange,
  showScoring = false,
  rubricId,
  typeC,
  'data-testid': testId = 'rubric-criteria-builder',
}: RubricCriteriaBuilderProps) => {
  const { t } = useTranslation();
  const isCustom = typeC === 'custom';
  const levelStartId = getLevelStartId(typeC);

  const state = useMemo<RubricCriteriaState>(() => {
    if (isValidState(value)) {
      return {
        ...value,
        settings: normalizeSettings(value.settings),
        criteria: normalizeCriteria(value.criteria, typeC),
      };
    }
    return getDefaultState(typeC);
  }, [value, typeC]);

  // v3 emitted its state on mount (update-count effect) so the form always
  // carries a criteria value; seed the default when the incoming value is
  // empty/invalid. Stops once the parent stores a valid state.
  useEffect(() => {
    if (!isValidState(value)) {
      onChange(getDefaultState(typeC));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value, typeC]);

  const commit = (criteria: RubricCriterion[]) => {
    onChange({ ...state, criteria: withTitles(criteria) });
  };

  // ── Level handlers (apply across every active criterion) ─────────────────

  const changeLevelLabel = (levelIndex: number, val: string) => {
    commit(
      state.criteria.map((criterion) => {
        const levels = criterion.levels_json as RubricLevel[];
        if (criterion._destroy || !Array.isArray(levels) || !levels[levelIndex]) return criterion;
        return {
          ...criterion,
          levels_json: levels.map((level, i) =>
            i === levelIndex ? { ...level, level_label: val } : level
          ),
        };
      })
    );
  };

  const changeLevelPoints = (levelIndex: number, val: string) => {
    // v3 InputNumber emitted number|null — level_points must be NUMERIC on the
    // wire (downstream grading copies it into score arithmetic). '' when cleared.
    const parsedValue = val === '' || Number.isNaN(Number(val)) ? '' : Number(val);
    commit(
      state.criteria.map((criterion) => {
        const levels = criterion.levels_json as RubricLevel[];
        if (criterion._destroy || !Array.isArray(levels) || !levels[levelIndex]) return criterion;
        return {
          ...criterion,
          levels_json: levels.map((level, i) =>
            i === levelIndex ? { ...level, level_points: parsedValue } : level
          ),
        };
      })
    );
  };

  const addLevel = () => {
    commit(
      state.criteria.map((criterion) => {
        if (criterion._destroy) return criterion;
        const levels = (criterion.levels_json as RubricLevel[]) ?? [];
        return { ...criterion, levels_json: [...levels, emptyLevel(levels.length + levelStartId)] };
      })
    );
  };

  const removeLevel = (levelIndex: number) => {
    const firstCriterion = state.criteria.find((criterion) => !criterion._destroy);
    const firstLevels = (firstCriterion?.levels_json as RubricLevel[]) ?? [];
    if (firstLevels.length <= 1) return; // keep at least one level

    commit(
      state.criteria.map((criterion) => {
        if (criterion._destroy) return criterion;
        const levels = criterion.levels_json as RubricLevel[];
        const filtered = levels.filter((_level, i) => i !== levelIndex);
        return {
          ...criterion,
          // Custom: reindex to contiguous 1..N so removing a middle level
          // doesn't leave a gap that makes the ceiling ordinal unreachable.
          levels_json: isCustom ? filtered.map((level, i) => ({ ...level, id: i + 1 })) : filtered,
        };
      })
    );
  };

  // ── Criterion handlers ────────────────────────────────────────────────────

  const changeCriterionLabel = (criterionIndex: number, val: string) => {
    commit(
      state.criteria.map((criterion, i) =>
        i === criterionIndex ? { ...criterion, label: val } : criterion
      )
    );
  };

  const changeLevelDesc = (criterionIndex: number, levelIndex: number, val: string) => {
    commit(
      state.criteria.map((criterion, i) => {
        if (i !== criterionIndex) return criterion;
        const levels = criterion.levels_json as RubricLevel[];
        return {
          ...criterion,
          levels_json: levels.map((level, j) =>
            j === levelIndex ? { ...level, desc: val } : level
          ),
        };
      })
    );
  };

  const addCriterion = () => {
    const firstCriterion = state.criteria.find((criterion) => !criterion._destroy);
    const firstLevels = (firstCriterion?.levels_json as RubricLevel[]) ?? [];
    // Copy the shared level structure (one_level_for_all) — v3 parity.
    const newLevels = firstLevels.map((level, i) => ({
      id: i + levelStartId,
      // `??` not `||` — a legitimate 0-point level must survive the copy
      // (grading falls back to the level ORDINAL when points are blank).
      level_points: level.level_points ?? '',
      level_label: level.level_label || '',
      desc: '',
    }));
    const newCriterion: RubricCriterion = {
      title: '',
      label: '',
      desc: '',
      strands: [],
      levels_json: newLevels.length > 0 ? newLevels : [emptyLevel(levelStartId)],
      id: getUniqueId(10),
      _isNew: true, // internal flag — stripped before submit
    };
    if (rubricId) {
      newCriterion.rubric_id = parseInt(String(rubricId), 10);
    }
    commit([...state.criteria.map((criterion) => ({ ...criterion })), newCriterion]);
  };

  const removeCriterion = (criterionIndex: number) => {
    const activeCriteria = state.criteria.filter((criterion) => !criterion._destroy);
    if (activeCriteria.length <= 1) return; // keep at least one criterion
    // Soft delete with _destroy (API compatibility — v3 parity).
    commit(
      state.criteria.map((criterion, i) =>
        i === criterionIndex ? { ...criterion, _destroy: true } : { ...criterion }
      )
    );
  };

  // ── Computed ──────────────────────────────────────────────────────────────

  const activeCriteria = state.criteria.filter((criterion) => !criterion._destroy);
  const levels = Array.isArray(activeCriteria[0]?.levels_json)
    ? (activeCriteria[0]?.levels_json as RubricLevel[])
    : [];
  const gridTemplateColumns =
    levels.length === 0
      ? '1fr'
      : `12rem ${levels
          .map(() => (showScoring ? 'minmax(10rem,1fr) 5rem' : 'minmax(10rem,1fr)'))
          .join(' ')}`;

  return (
    <div className="grid gap-3" data-testid={testId}>
      <div className="overflow-x-auto rounded-lg border">
        <div className="min-w-fit">
          {/* Header row — level labels (+ points when scoring) */}
          <div
            className="grid border-b bg-ap-muted-a50"
            // Dynamic column count — computed layout, not a style value.
            style={{ gridTemplateColumns }}
          >
            <div className="flex items-center border-e p-2 text-sm font-medium">
              {t('RubricsCustomComponent.criteria')}
            </div>
            {levels.map((level, levelIndex) => (
              <div key={`level-${levelIndex}`} className="contents">
                <div className="relative border-e p-2">
                  {levels.length > 1 && (
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon-sm"
                      className="absolute end-1 top-1 size-5"
                      aria-label={t('CommonText.remove')}
                      onClick={() => removeLevel(levelIndex)}
                      data-testid={`rubric-level-remove-${levelIndex}`}
                    >
                      <CloseSquareLinear aria-hidden="true" />
                    </Button>
                  )}
                  <Input
                    value={level.level_label}
                    placeholder={t('RubricLevelHeader.level_title')}
                    onChange={(event) => changeLevelLabel(levelIndex, event.target.value)}
                    className="border-0 shadow-none focus-visible:ring-1"
                    data-testid={`rubric-level-label-${levelIndex}`}
                  />
                </div>
                {showScoring && (
                  <div className="border-e p-2">
                    <Input
                      type="number"
                      inputMode="decimal"
                      min={0}
                      value={level.level_points ?? ''}
                      placeholder="0"
                      onChange={(event) => changeLevelPoints(levelIndex, event.target.value)}
                      className="border-0 tabular-nums shadow-none focus-visible:ring-1"
                      data-testid={`rubric-level-points-${levelIndex}`}
                    />
                  </div>
                )}
              </div>
            ))}
          </div>

          {/* Criterion rows */}
          {activeCriteria.map((criterion) => {
            const actualIndex = state.criteria.findIndex((c) => c.id === criterion.id);
            const criterionLevels = Array.isArray(criterion.levels_json)
              ? (criterion.levels_json as RubricLevel[])
              : [];
            return (
              <div
                key={String(criterion.id ?? actualIndex)}
                className="grid border-b last:border-b-0"
                style={{ gridTemplateColumns }}
                data-testid="rubric-criterion-row"
              >
                <div className="relative border-e p-2">
                  {activeCriteria.length > 1 && (
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon-sm"
                      className="absolute end-1 top-1 size-5"
                      aria-label={t('CommonText.remove')}
                      onClick={() => removeCriterion(actualIndex)}
                      data-testid={`rubric-criterion-remove-${actualIndex}`}
                    >
                      <CloseSquareLinear aria-hidden="true" />
                    </Button>
                  )}
                  <p className="text-sm font-semibold">{criterion.title}</p>
                  <Textarea
                    value={criterion.label ?? ''}
                    placeholder={t('RubricCriterionCell.criterion_title')}
                    onChange={(event) => changeCriterionLabel(actualIndex, event.target.value)}
                    rows={2}
                    className="mt-1 min-h-0 resize-none border-0 shadow-none focus-visible:ring-1"
                    data-testid={`rubric-criterion-label-${actualIndex}`}
                  />
                </div>
                {criterionLevels.map((level, levelIndex) => (
                  <div
                    key={`${criterion.id}-${levelIndex}`}
                    className="border-e p-2"
                    style={showScoring ? { gridColumn: 'span 2' } : undefined}
                  >
                    <Textarea
                      value={removeHTMLTagsFromText(level.desc, true)}
                      placeholder={t('CommonText.description')}
                      onChange={(event) =>
                        changeLevelDesc(actualIndex, levelIndex, event.target.value)
                      }
                      rows={3}
                      className="min-h-0 resize-none border-0 shadow-none focus-visible:ring-1"
                    />
                  </div>
                ))}
              </div>
            );
          })}
        </div>
      </div>

      <div className="flex gap-2">
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={addCriterion}
          data-testid="rubric-add-criterion"
        >
          <AddSquareLinear aria-hidden="true" />
          {t('RubricsCustomComponent.add_criterion')}
        </Button>
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={addLevel}
          data-testid="rubric-add-level"
        >
          <AddSquareLinear aria-hidden="true" />
          {t('RubricsCustomComponent.add_level')}
        </Button>
      </div>
    </div>
  );
};
